import React, { useEffect, useRef, useCallback, Fragment } from 'react';
import classNames from 'classnames';
import { createPortal } from 'react-dom';

export interface DialogProps {
  isOpen: boolean;
  onClose?: () => void;
  onRequestClose?: () => void;
  closable?: boolean;
  width?: number | string;
  height?: number | string;
  contentClassName?: string;
  bodyClassName?: string;
  overlayClassName?: string;
  title?: React.ReactNode;
  footer?: React.ReactNode;
  children?: React.ReactNode;
  closeOnOverlayClick?: boolean;
  closeOnEsc?: boolean;
}

const Dialog: React.FC<DialogProps> = ({
  isOpen,
  onClose,
  onRequestClose,
  closable = true,
  width = 520,
  height,
  contentClassName,
  bodyClassName,
  overlayClassName,
  title,
  footer,
  children,
  closeOnOverlayClick = true,
  closeOnEsc = true,
}) => {
  const dialogRef = useRef<HTMLDivElement>(null);
  const handleClose = onClose || onRequestClose;

  const handleEsc = useCallback(
    (event: KeyboardEvent) => {
      if (closeOnEsc && event.key === 'Escape' && closable && handleClose) {
        handleClose();
      }
    },
    [closeOnEsc, closable, handleClose]
  );

  const handleOverlayClick = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      if (
        closeOnOverlayClick &&
        closable &&
        handleClose &&
        event.target === event.currentTarget
      ) {
        handleClose();
      }
    },
    [closeOnOverlayClick, closable, handleClose]
  );

  useEffect(() => {
    if (isOpen) {
      document.addEventListener('keydown', handleEsc);
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.removeEventListener('keydown', handleEsc);
      document.body.style.overflow = '';
    };
  }, [isOpen, handleEsc]);

  if (!isOpen) return null;

  const dialogContent = (
    <div
      className={classNames('ecme-dialog-overlay', overlayClassName)}
      onClick={handleOverlayClick}
    >
      <div
        ref={dialogRef}
        className={classNames('ecme-dialog-content', contentClassName)}
        style={{
          width: typeof width === 'number' ? `${width}px` : width,
          height: height ? (typeof height === 'number' ? `${height}px` : height) : undefined,
        }}
        role="dialog"
        aria-modal="true"
      >
        {title && (
          <div className="ecme-dialog-header">
            <h3 className="ecme-dialog-title">{title}</h3>
            {closable && (
              <button
                type="button"
                className="ecme-dialog-close"
                onClick={handleClose}
                aria-label="Close"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                  <path
                    fillRule="evenodd"
                    d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                    clipRule="evenodd"
                  />
                </svg>
              </button>
            )}
          </div>
        )}
        <div className={classNames('ecme-dialog-body', bodyClassName)}>
          {children}
        </div>
        {footer && <div className="ecme-dialog-footer">{footer}</div>}
      </div>
    </div>
  );

  return createPortal(dialogContent, document.body);
};

export default Dialog;
